import { useMemo, useRef } from 'react';

import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import type { Form } from '../fixtures/forms.js';
import type { Message } from '../fixtures/domain.js';
import { MessageThread } from '../conversation/MessageThread.js';
import { isSendableReply, type AutoReply } from './AutoReply.js';
import styles from './AutoReplyPreview.module.css';

/**
 * One line of theirs, so the reply has something to be a reply to.
 *
 * Without it the pane shows a message arriving out of nowhere, which is the one
 * thing an auto reply is not: it answers somebody. It is the ordinary opener,
 * because the reply goes to whoever writes first and most of them write this.
 */
const OPENER = 'Hi, I would like to apply for this role.';

/** The two sides of a conversation that does not exist. Only `isOwnMessage`
 *  reads them, and only to decide which way each bubble faces. */
const THEM = 'auto-reply-preview-jobseeker';
const US = 'auto-reply-preview-employer';

/** Nothing in here can be pressed into sending anything. The thread asks for
 *  this because any bubble may carry a CTA row; none of these do. */
const noSend = () => Promise.resolve();

/**
 * What the person on the other end actually receives.
 *
 * The thread itself, not a drawing of one: the same `MessageThread` the
 * conversation pane runs, so the day pill, the grouping, the bubbles, the
 * automatic-reply head, the clock and the receipt are the ones production
 * draws. It used to be a hand-built pair of bubbles beside a dashed grey line
 * standing in for the jobseeker — an approximation stops matching the thread
 * the first time either changes, and the one question this pane exists to
 * answer is "is that what they will see".
 *
 * Two messages, forged here and nowhere near the store: their opener and the
 * reply it triggers. The reply carries the form when one is attached, exactly
 * as sending does, so the card under the words is the card that arrives.
 */
export function AutoReplyPreview({ reply, form }: { reply: AutoReply; form: Form | null }) {
  /**
   * One clock for as long as the pane is open. Read once into a ref: the pane
   * redraws on every keystroke in the settings column, and a timestamp taken
   * per render would give the two bubbles different minutes the moment one
   * ticked over mid-sentence — and a new array identity for the thread to
   * treat as a message arriving.
   */
  const sentAt = useRef(new Date().toISOString()).current;

  const messages = useMemo<Message[]>(() => {
    const both = {
      channelId: 'auto-reply-preview',
      createdAt: sentAt,
      // Sent, not read. A preview cannot know the jobseeker has read anything,
      // and the second tick is what starts the deletion clock — not a thing to
      // draw on a specimen.
      status: 'sent' as const,
      system: false,
    };
    return [
      { ...both, id: 'auto-reply-preview-opener', senderId: THEM, text: OPENER },
      {
        ...both,
        id: 'auto-reply-preview-reply',
        senderId: US,
        text: reply.message,
        autoReply: true,
        // The notice as sending freezes it (see data/forms' snapshotOf, whose
        // domain Form this fixtures Form is not).
        form: form ? { formId: form.id, name: form.name, fields: form.fields } : undefined,
      },
    ];
  }, [reply.message, form, sentAt]);

  if (!isSendableReply(reply)) {
    return (
      <section className={styles.pane} aria-label="Auto reply preview">
        <div className={styles.head}>
          <h2 className={styles.title}>Preview</h2>
        </div>
        <div className={styles.blank}>
          <EmptyState
            icon={<Icon name="bolt" size={32} />}
            title={reply.enabled ? 'Nothing to send yet' : 'Auto reply is off'}
            description={
              reply.enabled
                ? 'Write a message and it appears here as it will arrive.'
                : 'Switch it on to see how it lands.'
            }
          />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.pane} aria-label="Auto reply preview">
      {/* The title and nothing else, in the band the settings column wears, so
          the rules under them run as one line across the divider. The sentence
          that sat under it ("How this lands in a new chat.") is said by the
          thread below, by being one. */}
      <div className={styles.head}>
        <h2 className={styles.title}>Preview</h2>
      </div>

      <MessageThread
        messages={messages}
        loading={false}
        isOwnMessage={(message) => message.senderId === US}
        className={styles.thread!}
        currentUserId={US}
        sendReply={noSend}
        counterpartName="Jobseeker"
        // No onReplyTo: there is nothing here to answer. The words are changed
        // in the column beside this one.
      />
    </section>
  );
}
